import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { authedV3Request } from "utils/api/requests";
import { selectAuthToken } from "containers/App/selectors";

/*
 * The account's booth code: one 6-digit code that opens the settings of every
 * iPad on the account (rails `Api::V3::BoothCodesController`).
 *
 *   GET /api/v3/booth_code -> { set, version, set_at }   never the code
 *   PUT /api/v3/booth_code { booth_code } -> status + recovery_code (once)
 *
 * The token is part of the query key so the cache is per identity.
 */
const statusKey = (authToken) => ["account", "boothCode", authToken];

export const useBoothCodeStatus = () => {
  const authToken = useSelector(selectAuthToken());
  return useQuery({
    queryFn: () => authedV3Request("v3/booth_code", authToken),
    queryKey: statusKey(authToken),
    retry: 0,
    enabled: !!authToken,
  });
};

/*
 * Set or rotate the code. The response's `recovery_code` is shown once: the
 * caller keeps it in component state only. `cacheTime: 0` drops the mutation
 * result from React Query's cache as soon as nothing observes it, and only the
 * status fields are written into the status query.
 */
export const useSetBoothCode = () => {
  const authToken = useSelector(selectAuthToken());
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["account", "setBoothCode"],
    cacheTime: 0,
    mutationFn: (boothCode) => {
      if (!authToken) throw new Error("No auth token available");
      return authedV3Request("v3/booth_code", authToken, {
        method: "PUT",
        // Always a string: a JSON number would drop leading zeros.
        body: JSON.stringify({ booth_code: String(boothCode) }),
      });
    },
    onSuccess: ({ set, version, set_at: setAt }) => {
      queryClient.setQueryData(statusKey(authToken), { set, version, set_at: setAt });
    },
  });
};
